import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Customer, Order } from '../../../../../models/orderModel';
import { AdminService } from '../../../../../services/admin.service';
import { BackOfficeService } from '../../../../../services/backoffice.service';

@Component({
  selector: 'app-back-office24-order-edit',
  templateUrl: './back-office24-order-edit.component.html',
  styleUrl: './back-office24-order-edit.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})

export class BackOffice24OrderEditComponent implements OnInit, OnDestroy {
  @Input() orderId: string;
  @Input() showAdress: boolean;
  @Output() saveChangesEvent = new EventEmitter<void>();
  customer: Customer;
  order: Order;
  isDisabled = true;
  editCustomerForm: UntypedFormGroup;
  componentDestroyed$: Subject<boolean> = new Subject();
  isLoading = false;
  activeRequests: number = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public params: any,
    private fb: UntypedFormBuilder,
    public snackBar: MatSnackBar,
    public adminService: AdminService,
    public backofficeService: BackOfficeService // Assuming backofficeService is the same as adminService
  ) { }

  ngOnInit(): void {

    if (this.showAdress == undefined) {
      this.showAdress = true;
    }

    if (this.params) {
      this.orderId = this.params.orderId;
      this.showAdress = this.params.showAdress;
    }

    this.initEmptyCustomerForm();
    this.getOrder(this.orderId);
  }

  private setLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  private incrementRequests() {
    this.activeRequests++;
    this.setLoadingState(true);
  }

  private decrementRequests() {
    if (this.activeRequests > 0)
      this.activeRequests--;
    if (this.activeRequests === 0) {
      this.setLoadingState(false);
    }
  }

  initEmptyCustomerForm() {
    this.editCustomerForm = this.fb.group({
      customerId: [''],
      firstName: [''],
      lastName: [''],
      cellular: [''],
      email: ['']
    });
  }

  initEditCustomerForm() {
    this.editCustomerForm = this.fb.group({
      customerId: [this.order.customer.customerId],
      firstName: [this.order.customer.firstName],
      lastName: [this.order.customer.lastName],
      cellular: [this.order.customer.cellular],
      email: [this.order.customer.email]
    });
    const listChange = this.editCustomerForm.valueChanges;
    listChange
      .pipe(debounceTime(1000))
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((change: Customer) => {
        this.customer = change;
        this.isDisabled = false;
        this.snackBar.open(`Du har gjort ändringar, glöm inte att spara`, 'Tack!', {
          verticalPosition: 'top',
          duration: 2000
        });
      });
  }

  getOrder(orderId: string) {
    this.incrementRequests();

    this.backofficeService
      .getOrder(orderId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(order => {
        this.order = order;
        this.initEditCustomerForm();
        this.decrementRequests();
      }, () => {
        this.decrementRequests();
      });
  }

  saveChanges() {
    this.incrementRequests();

    this.backofficeService
      .editOrder(this.editCustomerForm.value)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        customer => {
          this.snackBar.open('Du har sparat ändringarna', 'OK', {
            verticalPosition: 'top',
            duration: 2000
          });
          this.isDisabled = true;
          this.saveChangesEvent.emit();
          this.decrementRequests();
        },
        error => {
          this.decrementRequests();
          this.snackBar.open(`Felkod: ${error.status}`, 'Doh!', {
            verticalPosition: 'top'
          });
        }
      );
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
